import { Client } from "@/Common/Api/Client";
import { AutoSavingForm, AutoSavingFormState, IAutoSavingFormProps } from "@/Common/Components/Forms/AutoSavingForm";
import { FormFieldRow } from "@/Common/Components/Forms/FormFieldRow";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { Locale } from "@/Common/Models/Localization/Locale";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { cacheStore } from "@/Globals/Stores/CacheStore";
import { WorkSafeBcPhysicianReportModel } from "./Models/WorkSafeBcPhysicianReportModel";
import { WorkSafeBcWorkerModel } from "./Models/WorkSafeBcWorkerModel";
import { GetWorkSafeBcPhysicianReportFormResponse } from "./ResponseModels/GetWorkSafeBcPhysicianReportFormResponse";
import { WorkSafeBcClinicalInformationSection } from "./SubComponent/WorkSafeBcClinicalInformationSection";
import { WorksSafeBcEmployeeSection } from "./SubComponent/WorkSafeBcEmployeeSection";
import { WorkSafeBcInjuryCodesSection } from "./SubComponent/WorkSafeBcInjuryCodesSection";
import { WorkSafeBcPayeeInformationSection } from "./SubComponent/WorkSafeBcPayeeInformationSection";
import { WorkSafeBcRegularPractitionerSection } from "./SubComponent/WorkSafeBcRegularPractitionerSection";
import { WorkSafeBcReturnToWorkPlanningSection } from "./SubComponent/WorkSafeBcReturnToWorkPlanningSection";
import { WorkSafeBcWorkerSection } from "./SubComponent/WorkSafeBcWorkerSection";
import { IBaseWorkSafeBcSubSectionProps } from "./BaseWorkSafeBcSubSection";

interface IWorkSafeBcPhysicianReportFormProps extends IAutoSavingFormProps<WorkSafeBcPhysicianReportModel> {
    isEditMode: boolean;
    worker: WorkSafeBcWorkerModel;
}

class WorkSafeBcPhysicianReportFormState extends AutoSavingFormState<WorkSafeBcPhysicianReportModel> {
    public physician: EmployeeUIModel;

    public constructor(init?: Partial<WorkSafeBcPhysicianReportFormState>) {
        super();
        Object.assign(this, init);
    }

    public static default(): WorkSafeBcPhysicianReportFormState {
        let physician = sessionStore.getCurrentEmployee();
        const title = physician.getIssuingOrgRule()?.Title;

        if (!EmployeeTitleHelper.isDoctor(title) && title !== EmployeeTitle.Resident)
            physician = null;

        return new WorkSafeBcPhysicianReportFormState({
            form: null,
            physician: physician
        });
    }
}

class WorkSafeBcPhysicianReportForm extends AutoSavingForm<WorkSafeBcPhysicianReportModel, IWorkSafeBcPhysicianReportFormProps, WorkSafeBcPhysicianReportFormState> {
    public constructor(props: IWorkSafeBcPhysicianReportFormProps) {
        super(props);
        this.state = WorkSafeBcPhysicianReportFormState.default();
        this.setAcceptableLocales([Locale.en]);
    }

    public componentWillMount(): void {
        this.getPhysicianReport();
    }

    public async saveForm(updateState: boolean = true): Promise<void> {
        if (!this.state.form.Metadata.HasPhysicianContributedToForm && this.state.physician)
            await this.mutateStateAsync(s => s.form.Metadata.HasPhysicianContributedToForm = true);

        await super.saveForm(updateState);
    }

    public render(): JSX.Element {
        if (!this.state?.form)
            return null;

        return (
            <div className="worksafebc-form">
                <div className="row-stretch">
                    <WorksSafeBcEmployeeSection {...this.getSubSectionProps()} />
                    <WorkSafeBcWorkerSection {...this.getSubSectionProps()} worker={this.props.worker} />
                </div>
                <WorkSafeBcRegularPractitionerSection
                    {...this.getSubSectionProps()}
                />
                <WorkSafeBcInjuryCodesSection
                    {...this.getSubSectionProps()}
                />
                <WorkSafeBcClinicalInformationSection
                    {...this.getSubSectionProps()}
                />
                <WorkSafeBcReturnToWorkPlanningSection
                    {...this.getSubSectionProps()}
                />
                <WorkSafeBcPayeeInformationSection
                    {...this.getSubSectionProps()}
                    firstPhysicianContributor={this.state.form.Metadata.HasPhysicianContributedToForm ? null : this.state.physician}
                />
                {this.renderAdditionalInformation()}
            </div>
        );
    }

    private renderAdditionalInformation(): JSX.Element {
        const data = this.state.form;

        return (
            <div className="flex-grow-container subsection-border-top">
                <FormFieldRow
                    labelValueKey="WorkSafeBc_PhysicianOnlyAdditionalInformationLabel"
                    isEditMode={this.props.isEditMode}
                    value={JSXHelper.BreakStringIntoParagraphs(data.AdditionalInformation)}
                >
                    <TextAreaResizable
                        id={`WorkSafeBC_PhysicianOnlyAdditionalInformation_${this.props.formId}`}
                        text={data.AdditionalInformation}
                        className="form-control textarea-resize-vertical"
                        onChange={value => this.mutateState(s => s.form.AdditionalInformation = value)}
                        rows={5}
                        maxRows={13}
                        maxLength={750}
                    />
                </FormFieldRow>
                {this.props.isEditMode && (
                    <div className="row margin-sm-bottom">
                        <div className="col-md-12">
                            <span className="required-star margin-xs-right">*</span>
                            <span>{cacheStore.GetString("RequiredFieldLabel")}</span>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private async getPhysicianReport(): Promise<void> {
        const response = await Client.postAsync<GetWorkSafeBcPhysicianReportFormResponse>(
            ServerOperations.Server.WorkSafeBc_GetPhysicianReportForm,
            { Id: this.props.formId }
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        this.mutateState(s => {
            s.form = new WorkSafeBcPhysicianReportModel(response.data.PhysicianReport);
            s.formSnapShot = JSON.stringify(s.form);
        });
    }

    private getSubSectionProps(): IBaseWorkSafeBcSubSectionProps {
        return {
            isEditMode: this.props.isEditMode,
            model: this.state.form,
            formId: this.props.formId,
            mutateModelState: mutation => this.mutateState(s => mutation(s.form))
        };
    }
}

export { WorkSafeBcPhysicianReportForm };
